import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SubscriptionLegalLinks } from '../../components/billing/SubscriptionLegalLinks';
import { InviteHouseholdModal } from '../../components/household/InviteHouseholdModal';
import { GlassButton } from '../../components/ui/GlassButton';
import { GlassCard } from '../../components/ui/GlassCard';
import { SampleDataBanner } from '../../components/ui/SampleDataBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { useSampleData } from '../../contexts/SampleDataContext';
import { navigateToAuth, navigateToLegal } from '../../core/navigation/navigationRef';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { useBillingActions } from '../../hooks/useBillingActions';
import { authService } from '../Auth/services/authService';
import { ValueStepper } from '../Onboarding/components/ValueStepper';
import { forcePullDashboards } from '../../services/analyticsService';
import { PRO_PRICE_USD } from '../../services/billingService';
import { leaveHousehold, removeHouseholdMember } from '../../services/householdService';
import { telemetry } from '../../services/telemetry';

const CURRENT_YEAR = new Date().getFullYear();
const MIN_BIRTH_YEAR = CURRENT_YEAR - 100;
const MAX_BIRTH_YEAR = CURRENT_YEAR - 16;
const DEFAULT_BIRTH_YEAR = CURRENT_YEAR - 35;
const DEFAULT_RETIREMENT_AGE = 65;

type Props = {
  onClose: () => void;
};

/**
 * Settings page body: subscription, notifications, retirement, household,
 * then Delete account at the bottom. Sign out lives in the account menu.
 * Guests and free accounts see Subscribe.
 */
export function SettingsModalContent({ onClose }: Props) {
  const insets = useSafeAreaInsets();
  const { isSampleMode } = useSampleData();
  const { isAuthenticated, user } = useAuth();
  const { upgradeToPro, manageSubscription, restorePurchases, usesAppleIap, busy: billingBusy, error: billingError } =
    useBillingActions();
  const [localBillingError, setLocalBillingError] = useState<string | null>(null);

  const isPro = user?.plan === 'pro';
  const billedOnApple = user?.billingProvider === 'apple' || Boolean(user?.hasAppleSubscription);
  const canManage = usesAppleIap
    ? Boolean(isPro && user?.hasAppleSubscription)
    : Boolean(user?.hasStripeCustomer && user?.billingProvider !== 'apple');
  const displayError = localBillingError ?? billingError;

  useEffect(() => {
    if (isPro) return;
    void telemetry.logPaywallView(isAuthenticated ? 'settings' : 'settings_guest');
  }, [isAuthenticated, isPro]);

  const handleUpgrade = useCallback(async () => {
    setLocalBillingError(null);
    void telemetry.logUpgradeCtaClick('settings');
    // Native: dismiss Settings before the system purchase sheet.
    // Web: stay here — Checkout navigates away; onClose() is goBackOrLanding (Home).
    if (usesAppleIap) {
      onClose();
    }
    try {
      await upgradeToPro('settings');
    } catch (err) {
      setLocalBillingError(err instanceof Error ? err.message : usesAppleIap ? 'Purchase failed' : 'Checkout failed');
    }
  }, [onClose, upgradeToPro, usesAppleIap]);

  const handleManage = useCallback(async () => {
    setLocalBillingError(null);
    try {
      await manageSubscription();
    } catch (err) {
      setLocalBillingError(
        err instanceof Error
          ? err.message
          : usesAppleIap
            ? 'Could not open Apple subscriptions'
            : 'Could not open portal',
      );
    }
  }, [manageSubscription, usesAppleIap]);

  const handleRestore = useCallback(async () => {
    setLocalBillingError(null);
    try {
      await restorePurchases();
    } catch (err) {
      setLocalBillingError(err instanceof Error ? err.message : 'Could not restore purchases');
    }
  }, [restorePurchases]);

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom, spacing.xl) + spacing.md },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
      >
        {isSampleMode && <SampleDataBanner compact />}

        {!isAuthenticated ? (
          <GlassCard>
            <Text style={styles.sectionTitle}>Subscription</Text>
            <Text style={styles.rowHint}>
              Pro is tied to your MoneyLens account so AI quota, household, and the same
              entitlement work on iPhone, iPad, and the website. Sign in with Apple works on
              iOS and moneylensai.ca and does not ask for extra personal fields beyond Apple’s
              sheet. Then subscribe to Pro (${PRO_PRICE_USD}/mo) — 200 AI analyses per billing
              period, AI chat, and household up to 50.
            </Text>
            <GlassButton
              variant="primary"
              fullWidth
              onPress={() => {
                void telemetry.logUpgradeCtaClick('settings_guest');
                onClose();
                navigateToAuth();
              }}
              style={styles.row}
            >
              Sign in to subscribe
            </GlassButton>
            <GlassButton
              variant="secondary"
              fullWidth
              onPress={() => {
                onClose();
                navigateToAuth();
              }}
              style={styles.row}
            >
              Sign in
            </GlassButton>
            <SubscriptionLegalLinks />
          </GlassCard>
        ) : (
          <>
            <GlassCard>
              <Text style={styles.sectionTitle}>Subscription</Text>
              <View style={styles.nameRow}>
                <Text style={styles.userName}>{user?.name ?? 'Signed in'}</Text>
                {isPro ? <Text style={styles.proPill}>Pro</Text> : <Text style={styles.freePill}>Free</Text>}
              </View>
              <Text style={styles.userMeta}>{user?.phoneNumber ?? user?.email ?? ''}</Text>

              <View style={styles.planRow}>
                <Text style={styles.planLabel}>{isPro ? 'Pro' : 'Free plan'}</Text>
                {isPro ? (
                  <Text style={styles.planBadge}>Active</Text>
                ) : (
                  <Text style={styles.planHint}>${PRO_PRICE_USD}/mo</Text>
                )}
              </View>

              {isPro ? (
                <Text style={styles.rowHint}>
                  200 AI analyses per billing period, AI chat, and household up to 50.
                </Text>
              ) : (
                <>
                  {typeof user?.remainingAnalyzes === 'number' &&
                  typeof user?.analyzeLimit === 'number' ? (
                    <Text style={styles.rowHint}>
                      {user.remainingAnalyzes} of {user.analyzeLimit} free AI analyses left
                    </Text>
                  ) : null}
                  <Text style={styles.rowHint}>
                    Pro is ${PRO_PRICE_USD}/mo — 200 AI analyses per billing period, AI
                    chat, and household up to 50. Not unlimited uploads.
                  </Text>
                </>
              )}

              {displayError ? <Text style={styles.errorText}>{displayError}</Text> : null}

              {!isPro ? (
                <GlassButton
                  variant="primary"
                  fullWidth
                  disabled={billingBusy}
                  onPress={handleUpgrade}
                  style={styles.row}
                >
                  {billingBusy ? (
                    <ActivityIndicator color={colors.text} />
                  ) : (
                    `Subscribe · $${PRO_PRICE_USD}/mo`
                  )}
                </GlassButton>
              ) : null}

              {usesAppleIap && !isPro ? (
                <Text style={styles.rowHint}>
                  Billed ${PRO_PRICE_USD}/month through your Apple ID. Auto-renews until canceled in
                  Apple ID subscriptions. Pro on this account also works on the website and Android
                  with the same login. Android stays on Stripe if you subscribe there instead.
                  Restore purchases if you already subscribed on this Apple ID.
                </Text>
              ) : null}

              {usesAppleIap && isPro && billedOnApple ? (
                <Text style={styles.rowHint}>
                  You’re Pro via the App Store. The same login has Pro on the website and Android.
                  Cancel in iPhone Settings → Apple ID → Subscriptions.
                </Text>
              ) : null}

              {usesAppleIap && isPro && user?.billingProvider === 'stripe' ? (
                <Text style={styles.rowHint}>
                  You’re Pro via website billing. This iPhone app does not open Stripe. Open
                  MoneyLens on the web to change payment.
                </Text>
              ) : null}

              {!usesAppleIap && isPro && billedOnApple ? (
                <Text style={styles.rowHint}>
                  You’re Pro via the iOS App Store. This login is entitled here too. Cancel or
                  change billing on iPhone (Settings → Apple ID → Subscriptions). Android uses
                  Stripe only for new purchases.
                </Text>
              ) : null}

              {!usesAppleIap && !isPro ? (
                <Text style={styles.rowHint}>
                  On Android and the web, Pro is billed with Stripe (${PRO_PRICE_USD}/mo).
                </Text>
              ) : null}

              {canManage ? (
                <GlassButton
                  variant={isPro ? 'primary' : 'secondary'}
                  fullWidth
                  disabled={billingBusy}
                  onPress={handleManage}
                  style={styles.row}
                >
                  {billingBusy ? <ActivityIndicator color={colors.text} /> : 'Manage subscription'}
                </GlassButton>
              ) : null}

              {usesAppleIap && isAuthenticated ? (
                <GlassButton
                  variant="secondary"
                  fullWidth
                  disabled={billingBusy}
                  onPress={() => {
                    void handleRestore();
                  }}
                  style={styles.row}
                >
                  {billingBusy ? <ActivityIndicator color={colors.text} /> : 'Restore purchases'}
                </GlassButton>
              ) : null}

              <SubscriptionLegalLinks />
            </GlassCard>

            <GlassCard>
              <NotificationPreferencesSection />
            </GlassCard>
            <GlassCard>
              <RetirementProfileSection />
            </GlassCard>
            <GlassCard>
              <HouseholdSettingsSection isPro={isPro} />
            </GlassCard>
            <GlassCard>
              <DeleteAccountSection />
            </GlassCard>
          </>
        )}
      </ScrollView>
    </View>
  );
}

function DeleteAccountSection() {
  return (
    <>
      <Text style={styles.rowLabel}>Delete account</Text>
      <Text style={styles.rowHint}>
        Deactivates sign-in on every device. Records stay stored (soft delete). Cancel App Store,
        Play, or Stripe billing separately.
      </Text>
      <GlassButton
        variant="ghost"
        fullWidth
        onPress={() => navigateToLegal('DeleteAccount')}
        textStyle={styles.dangerText}
        style={styles.row}
        accessibilityLabel="Delete account"
      >
        Delete account
      </GlassButton>
    </>
  );
}

/** Channels + contacts for spend alerts, insights, household — shared across types. */
function NotificationPreferencesSection() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const [push, setPush] = useState(true);
  const [emailOn, setEmailOn] = useState(false);
  const [spendAlerts, setSpendAlerts] = useState(true);
  const [insightReady, setInsightReady] = useState(true);
  const [household, setHousehold] = useState(true);
  const [emailDraft, setEmailDraft] = useState(user?.email ?? '');
  const [channelHints, setChannelHints] = useState<{
    push?: string;
    email?: string;
  }>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { getNotificationPreferences } = await import(
          '../../services/notificationPreferencesService'
        );
        const res = await getNotificationPreferences();
        if (cancelled) return;
        setPush(res.preferences.channels.push);
        setEmailOn(res.preferences.channels.email);
        setSpendAlerts(res.preferences.types.spend_alerts);
        setInsightReady(res.preferences.types.insight_ready);
        setHousehold(res.preferences.types.household);
        setEmailDraft(res.email ?? '');
        setChannelHints({
          push: res.channels.push.detail,
          email: res.channels.email.detail,
        });
      } catch {
        if (!cancelled) setError('Could not load notification settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedHint(null);
    try {
      const { putNotificationPreferences } = await import(
        '../../services/notificationPreferencesService'
      );
      const res = await putNotificationPreferences({
        channels: { push, email: emailOn, sms: false },
        types: {
          spend_alerts: spendAlerts,
          insight_ready: insightReady,
          household,
        },
        ...(emailDraft.trim() && emailDraft.trim() !== (user?.email ?? '')
          ? { emailAddress: emailDraft.trim() }
          : {}),
      });
      setChannelHints({
        push: res.channels.push.detail,
        email: res.channels.email.detail,
      });
      await refreshUser();
      setSavedHint('Notification settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [
    emailDraft,
    emailOn,
    household,
    insightReady,
    push,
    refreshUser,
    spendAlerts,
    user?.email,
  ]);

  return (
    <View>
      <Text style={styles.rowLabel}>Notifications</Text>
      <Text style={styles.rowHint}>
        Choose how MoneyLens reaches you — spend alerts, insights, and household updates share these
        channels.
      </Text>
      {loading ? (
        <ActivityIndicator color={colors.accent} />
      ) : (
        <>
          <Text style={styles.notifGroup}>Channels</Text>
          <ToggleRow label="Push" selected={push} onToggle={() => setPush((v) => !v)} hint={channelHints.push} />
          <ToggleRow label="Email" selected={emailOn} onToggle={() => setEmailOn((v) => !v)} hint={channelHints.email} />

          <Text style={styles.notifGroup}>Contacts</Text>
          <Text style={styles.rowHint}>Required when Email is on.</Text>
          <View style={styles.contactFields}>
            <TextInput
              value={emailDraft}
              onChangeText={setEmailDraft}
              placeholder="you@example.com"
              placeholderTextColor={colors.textMuted}
              keyboardType="email-address"
              autoCapitalize="none"
              returnKeyType="done"
              style={styles.contactInput}
              accessibilityLabel="Notification email"
            />
          </View>

          <Text style={styles.notifGroup}>Alert types</Text>
          <ToggleRow
            label="Spend / cut-spend alerts"
            selected={spendAlerts}
            onToggle={() => setSpendAlerts((v) => !v)}
          />
          <ToggleRow
            label="Insight ready"
            selected={insightReady}
            onToggle={() => setInsightReady((v) => !v)}
          />
          <ToggleRow
            label="Household updates"
            selected={household}
            onToggle={() => setHousehold((v) => !v)}
          />

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {savedHint ? <Text style={styles.savedHint}>{savedHint}</Text> : null}
          <GlassButton
            variant="secondary"
            fullWidth
            disabled={saving}
            onPress={() => {
              void handleSave();
            }}
            style={styles.row}
          >
            {saving ? <ActivityIndicator color={colors.text} /> : 'Save notifications'}
          </GlassButton>
        </>
      )}
    </View>
  );
}

function ToggleRow({
  label,
  selected,
  onToggle,
  hint,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
  hint?: string;
}) {
  return (
    <View style={styles.toggleBlock}>
      <GlassButton
        variant="secondary"
        size="sm"
        selected={selected}
        onPress={onToggle}
        accessibilityRole="switch"
        accessibilityState={{ checked: selected }}
        accessibilityLabel={label}
      >
        {selected ? `On · ${label}` : `Off · ${label}`}
      </GlassButton>
      {hint ? <Text style={styles.rowHint}>{hint}</Text> : null}
    </View>
  );
}

/** Birth year + retirement age — feeds retirement_gap insights. */
function RetirementProfileSection() {
  const { user, refreshUser } = useAuth();
  const [birthYear, setBirthYear] = useState(
    user?.meta?.birthYear ?? DEFAULT_BIRTH_YEAR,
  );
  const [retirementAge, setRetirementAge] = useState(
    user?.meta?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);

  useEffect(() => {
    setBirthYear(user?.meta?.birthYear ?? DEFAULT_BIRTH_YEAR);
    setRetirementAge(user?.meta?.retirementAge ?? DEFAULT_RETIREMENT_AGE);
  }, [user?.meta?.birthYear, user?.meta?.retirementAge]);

  const dirty =
    birthYear !== (user?.meta?.birthYear ?? DEFAULT_BIRTH_YEAR) ||
    retirementAge !== (user?.meta?.retirementAge ?? DEFAULT_RETIREMENT_AGE) ||
    user?.meta?.birthYear == null ||
    user?.meta?.retirementAge == null;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedHint(null);
    try {
      await authService.updateProfile({ birthYear, retirementAge });
      await refreshUser();
      setSavedHint('Saved — refresh insights to update your retirement card.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [birthYear, refreshUser, retirementAge]);

  return (
    <View>
      <Text style={styles.rowLabel}>Retirement planning</Text>
      <Text style={styles.rowHint}>
        Used for $1M / $2M nest-egg math. Defaults assume age 35 → retire 65 until you set these.
      </Text>
      <View style={styles.profileFields}>
        <ValueStepper
          label="Birth year"
          value={birthYear}
          min={MIN_BIRTH_YEAR}
          max={MAX_BIRTH_YEAR}
          onChange={setBirthYear}
          onDecrease={() => setBirthYear((y) => Math.max(MIN_BIRTH_YEAR, y - 1))}
          onIncrease={() => setBirthYear((y) => Math.min(MAX_BIRTH_YEAR, y + 1))}
        />
        <ValueStepper
          label="Retirement age"
          value={retirementAge}
          min={40}
          max={80}
          onChange={setRetirementAge}
          onDecrease={() => setRetirementAge((a) => Math.max(40, a - 1))}
          onIncrease={() => setRetirementAge((a) => Math.min(80, a + 1))}
        />
      </View>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
      {savedHint ? <Text style={styles.savedHint}>{savedHint}</Text> : null}
      <GlassButton
        variant="secondary"
        fullWidth
        disabled={saving || !dirty}
        onPress={() => {
          void handleSave();
        }}
        style={styles.row}
      >
        {saving ? <ActivityIndicator color={colors.text} /> : 'Save age & retirement'}
      </GlassButton>
    </View>
  );
}

function HouseholdSettingsSection({ isPro }: { isPro: boolean }) {
  const { household, loading, refresh, canInvite } = useHouseholdMembership();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const members = household?.members ?? [];
  const memberCount = household?.memberCount ?? members.length;
  const freeCap = household?.freeMemberCap ?? 2;
  const maxMembers = household?.maxMembers ?? freeCap;
  const householdHasPro = Boolean(household?.hasPro || isPro);
  const inviteBlockedReason = !canInvite
    ? memberCount >= maxMembers
      ? `Household is full (max ${maxMembers})`
      : memberCount >= freeCap && !householdHasPro
        ? 'Upgrade to Pro to invite more members (group household)'
        : 'Invites unavailable'
    : null;

  return (
    <>
      <Text style={styles.rowLabel}>Household</Text>
      {loading && !household ? (
        <ActivityIndicator color={colors.accent} />
      ) : (
        <>
          <Text style={styles.rowHint}>
            Budget with a partner — Mine stays personal; Household shows both.
            {members.length
              ? ` · ${members.length} member${members.length === 1 ? '' : 's'}`
              : ''}
            {household?.name ? ` · ${household.name}` : ''}
          </Text>
          {members.map((m) => (
            <View key={m.userId} style={styles.memberRow}>
              <Text style={styles.memberName}>
                {m.name} · {m.role}
              </Text>
              {household?.myRole === 'owner' && m.role !== 'owner' ? (
                <GlassButton
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onPress={() => {
                    setBusy(true);
                    void removeHouseholdMember(m.userId)
                      .then(async () => {
                        await refresh();
                        await forcePullDashboards({ reason: 'household_member_removed' });
                      })
                      .finally(() => setBusy(false));
                  }}
                >
                  Remove
                </GlassButton>
              ) : null}
            </View>
          ))}
          {household?.myRole === 'member' ? (
            <GlassButton
              variant="ghost"
              fullWidth
              disabled={busy}
              onPress={() => {
                setBusy(true);
                void leaveHousehold()
                  .then(async () => {
                    await refresh();
                    await forcePullDashboards({ reason: 'household_left' });
                  })
                  .finally(() => setBusy(false));
              }}
              style={styles.row}
            >
              Leave household
            </GlassButton>
          ) : null}
        </>
      )}
      {inviteBlockedReason ? (
        <Text style={styles.rowHint}>{inviteBlockedReason}</Text>
      ) : null}
      <GlassButton
        variant="secondary"
        fullWidth
        disabled={!canInvite}
        onPress={() => setInviteOpen(true)}
        style={styles.row}
      >
        Invite member
      </GlassButton>
      <InviteHouseholdModal visible={inviteOpen} onClose={() => setInviteOpen(false)} />
    </>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
  },
  sectionTitle: {
    ...typography.headline,
    color: colors.text,
    marginBottom: spacing.md,
    fontSize: 16,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: 2,
  },
  userName: {
    ...typography.headline,
    color: colors.text,
  },
  proPill: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    overflow: 'hidden',
  },
  freePill: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  userMeta: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.md,
  },
  planRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  planLabel: {
    ...typography.headline,
    fontSize: 20,
    color: colors.text,
  },
  planBadge: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  planHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  row: {
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  rowLabel: {
    ...typography.body,
    color: colors.text,
  },
  rowHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 2,
  },
  errorText: {
    ...typography.caption,
    color: colors.danger,
    marginTop: spacing.xs,
  },
  dangerText: {
    color: colors.danger,
    fontWeight: '700',
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  memberName: {
    ...typography.caption,
    color: colors.text,
    flex: 1,
  },
  profileFields: {
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  savedHint: {
    ...typography.caption,
    color: colors.accent,
    marginTop: spacing.xs,
  },
  notifGroup: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  toggleBlock: {
    gap: 4,
    marginTop: spacing.xs,
  },
  contactFields: {
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  contactInput: {
    ...typography.body,
    fontSize: 14,
    color: colors.text,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: colors.surfaceElevated,
  },
});
